import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Text,
  VStack,
  Image,
  useToast,
  Fade,
  ScaleFade,
  Divider,
  Select,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { login, verifyOtp } from "../actions/userActions";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "./images/logo/Vmuktilogo.png";
import loginBg from "./images/newbg-bg.jpg";
import ThemeToggle from "./ThemeToggle";

const Login = () => {
  // ── Theme-aware styling ────────────────────────────────────────────────
  // Single background (newbg) used in both modes — only the glass card and
  // scrim adapt. The image itself was sampled: it has a bright warm streak
  // around 15-45% height (luminance up to ~170/255), so the scrim leans on
  // darkening that band a bit more in dark mode — otherwise a translucent
  // dark card sitting over that bright streak would wash out its own text.
  const bgImageSrc = loginBg;
  const heroOverlay = useColorModeValue(
    "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 40%, rgba(226,232,240,0.30) 100%)",
    "linear-gradient(180deg, rgba(3,7,18,0.42) 0%, rgba(3,7,18,0.34) 40%, rgba(3,7,18,0.58) 100%)",
  );
  // Glass card: much more see-through than a normal surface — the artwork
  // should read clearly behind the text, not just tint it.
  const cardBg = useColorModeValue("rgba(255,255,255,0.42)", "rgba(15,20,30,0.42)");
  const cardBorder = useColorModeValue(
    "1px solid rgba(255,255,255,0.65)",
    "1px solid rgba(255,255,255,0.15)",
  );
  const cardShadow = useColorModeValue(
    "0 8px 32px rgba(15,23,42,0.18)",
    "0 8px 32px rgba(0,0,0,0.45)",
  );
  const cardBlur = "blur(1px) saturate(150%)";
  // Form fields sit one layer "more solid" than the card so they stay
  // readable, but are still translucent — same frosted-glass material.
  const fieldBg = useColorModeValue("rgba(255,255,255,0.50)", "rgba(255,255,255,0.06)");
  const fieldBorder = useColorModeValue("rgba(255,255,255,0.75)", "rgba(255,255,255,0.16)");
  const fieldAddonBg = useColorModeValue("rgba(255,255,255,0.32)", "rgba(255,255,255,0.04)");
  const toggleBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.100");
  const toggleBorder = useColorModeValue("gray.200", "whiteAlpha.300");
  const dangerColor = useColorModeValue("red.600", "red.300");
  // Explicit black/white (not the muted "text-secondary" token) — needed for
  // legibility against the transparent glass card, which shows a busy image
  // behind it rather than a flat surface color.
  const strongTextColor = useColorModeValue("black", "white");

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [mobileForOtp, setMobileForOtp] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phase, setPhase] = useState("");
  const [selectedState, setSelectedState] = useState("Gujarat");
  const [selectedDistrict, setSelectedDistrict] = useState("");

  const stateData = {
    "Gujarat": {
      phase1: ["AHMEDABAD"],
    }
  };

  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      setLocationEnabled(true);
    } else {
      toast({
        title: "Location Error",
        description: "Please enable location to use this application.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    const userRole = localStorage.getItem("role");

    if (isLoggedIn) {
      if (userRole === "district") {
        navigate("/head");
      } else {
        navigate("/autoinstaller");
      }
    }
  }, []);

  const handleMobileChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setMobile(value);
    }
  };

  const handleSendOtp = async () => {
    if (!name || mobile.length < 10) {
      toast({
        title: "Invalid Input",
        description:
          "Please enter your name and a valid 10-digit mobile number.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedState || !selectedDistrict || !phase) {
      toast({
        title: "Selection Required",
        description: "Please select both State and District before proceeding.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      setMobileForOtp(mobile);
      const response = await login(name, mobile, phase);
      if (response && response.success !== false) {
        setOtpSent(true);
        setResendDisabled(true);
        setResendTimer(30);
        toast({
          title: "OTP Sent",
          description: "An OTP has been sent to your mobile number.",
          status: "success",
          duration: 3000,
        });
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to send OTP.",
          status: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    const otpString = otp.join("");
    if (otpString.length < 6) {
      toast({
        title: "Incomplete OTP",
        description: "Please enter the full 6-digit OTP.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await verifyOtp(mobile, otpString, phase);
      if (response && response.success) {
        localStorage.setItem("name", name);
        localStorage.setItem("mobile", mobile);
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("token", response.token);
        localStorage.setItem("role", response.role);
        localStorage.setItem("phase", response.phase || phase); //added the phase to local storage
        localStorage.setItem("state", selectedState); // persist selected state (e.g. Bihar)

        toast({
          title: "Success",
          description: "Login successful!",
          status: "success",
          duration: 2000,
        });

        if (response.role === "district") {
          navigate("/head");
        } else if (response.role === "checkpost") {
          navigate("/attendance");
        } else if (response.role === "punjabInstaller") {
          navigate("/punjabInstaller");
        } else {
          navigate("/autoinstaller");
        }
      } else {
        toast({
          title: "Login Failed",
          description: "Invalid OTP or Mobile Number.",
          status: "error",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (/^\d*$/.test(value) && value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      bgImage={`url(${bgImageSrc})`}
      bgSize="cover"
      bgPosition="center"
      bgRepeat="no-repeat"
      p={4}
      pt="max(1rem, env(safe-area-inset-top))"
      pb="max(1rem, env(safe-area-inset-bottom))"
    >
      {/* Scrim so the card stays legible over the photo, tuned per theme */}
      <Box position="absolute" inset={0} bgImage={heroOverlay} pointerEvents="none" />

      {/* Theme toggle — frosted so it stays visible over the artwork in both modes */}
      <ThemeToggle
        position="absolute"
        top="max(1rem, env(safe-area-inset-top))"
        right={4}
        zIndex={2}
        bg={toggleBg}
        border="1px solid"
        borderColor={toggleBorder}
        backdropFilter="blur(12px)"
      />

      <Container maxW={{ base: "95%", sm: "450px" }} zIndex={1} px={0}>
        <ScaleFade initialScale={0.9} in={true}>
          <VStack
            spacing={{ base: 6, md: 8 }}
            bg={cardBg}
            backdropFilter={cardBlur}
            p={{ base: 6, sm: 8, md: 10 }}
            borderRadius={{ base: "2xl", md: "3xl" }}
            boxShadow={cardShadow}
            border={cardBorder}
            textAlign="center"
            w="full"
          >
            <VStack spacing={3}>
              <Image
                src={logo}
                h={{ base: "40px", md: "50px" }}
                mb={2}
                alt="VMukti Logo"
              />
              <Heading
                as="h1"
                size={{ base: "md", md: "lg" }}
                color="text-primary"
                fontWeight="800"
                letterSpacing="-0.5px"
              >
                Election Installer App
              </Heading>
              <Text color={strongTextColor} fontSize={{ base: "sm", md: "md" }}>
                Securely access your account to proceed
              </Text>
            </VStack>

            {!otpSent ? (
              <VStack spacing={5} w="full">
                <FormControl isRequired>
                    <FormLabel fontWeight="700" color="text-primary" fontSize="sm">
                      Full Name
                    </FormLabel>
                    <Input
                      placeholder="Enter your name"
                      bg={fieldBg}
                      size="md"
                      borderRadius="xl"
                      borderColor={fieldBorder}
                      boxShadow="sm"
                      _focus={{
                        borderColor: "brand-primary",
                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-primary)",
                      }}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontWeight="700" color="text-primary" fontSize="sm">
                      Mobile Number
                    </FormLabel>
                    <InputGroup size="md">
                      <InputLeftAddon
                        borderLeftRadius="xl"
                        bg={fieldAddonBg}
                        color="text-secondary"
                        borderColor={fieldBorder}
                        fontWeight="600"
                      >
                        +91
                      </InputLeftAddon>
                      <Input
                        type="tel"
                        placeholder="Enter mobile number"
                        bg={fieldBg}
                        size="md"
                        borderRightRadius="xl"
                        borderColor={fieldBorder}
                        boxShadow="sm"
                        _focus={{
                          borderColor: "brand-primary",
                          boxShadow: "0 0 0 1px var(--chakra-colors-brand-primary)",
                        }}
                        value={mobile}
                        onChange={handleMobileChange}
                      />
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontWeight="700" color="text-primary" fontSize="sm">
                      Select State
                    </FormLabel>
                    <Select
                      placeholder="Choose State"
                      bg={fieldBg}
                      size="md"
                      borderRadius="xl"
                      borderColor={fieldBorder}
                      boxShadow="sm"
                      _focus={{
                        borderColor: "brand-primary",
                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-primary)",
                      }}
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setSelectedDistrict("");
                        setPhase("");
                      }}
                    >
                      {Object.keys(stateData).map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedState && (
                    <FormControl isRequired>
                      <FormLabel fontWeight="700" color="text-primary" fontSize="sm">
                        Select District
                      </FormLabel>
                      <Select
                        placeholder="Choose District"
                        bg={fieldBg}
                        size="md"
                        borderRadius="xl"
                        borderColor={fieldBorder}
                        boxShadow="sm"
                        _focus={{
                          borderColor: "brand-primary",
                          boxShadow: "0 0 0 1px var(--chakra-colors-brand-primary)",
                        }}
                        value={selectedDistrict}
                        onChange={(e) => {
                          const district = e.target.value;
                          setSelectedDistrict(district);
                          // Find the phase for the selected district
                          const statePhases = stateData[selectedState];
                          for (const p in statePhases) {
                            if (statePhases[p].includes(district)) {
                              setPhase(p);
                              break;
                            }
                          }
                        }}
                      >
                        {Object.entries(stateData[selectedState]).flatMap(
                          ([p, districts]) =>
                            districts.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            )),
                        )}
                      </Select>
                    </FormControl>
                  )}

                  <Button
                    w="full"
                    className="btn-premium"
                    sx={{
                      backgroundColor: "#4f46e5 !important",
                      color: "white !important",
                      _hover: {
                        backgroundColor: "#4338ca !important",
                        transform: "translateY(-2px)",
                        boxShadow: "xl",
                      },
                      _active: {
                        backgroundColor: "#3730a3 !important",
                        transform: "translateY(0)",
                      },
                    }}
                    size="lg"
                    height={{ base: "50px", md: "56px" }}
                    borderRadius="xl"
                    fontSize="md"
                    fontWeight="bold"
                    boxShadow="lg"
                    top={5}
                    onClick={handleSendOtp}
                    isLoading={isLoading}
                    isDisabled={
                      !locationEnabled || !selectedState || !selectedDistrict
                    }
                  >
                    Get Verification Code
                  </Button>

                  {!locationEnabled && (
                    <Text color={dangerColor} fontSize="xs" fontWeight="600">
                      * Please enable location to continue
                    </Text>
                  )}
                </VStack>
              ) : (
                <Fade in={otpSent} style={{ width: "100%" }}>
                  <VStack spacing={6} w="full">
                    <VStack spacing={1}>
                      <Text
                        fontWeight="800"
                        fontSize={{ base: "lg", md: "xl" }}
                        color="text-primary"
                      >
                        Verify Identity
                      </Text>
                      <Text color={strongTextColor} fontSize="sm">
                        Enter the 6-digit code sent to
                      </Text>
                      <Text fontWeight="800" color="accent-text">
                        +91 - {mobile}
                      </Text>
                    </VStack>

                    <HStack
                      spacing={{ base: 1, sm: 2 }}
                      justify="center"
                      w="full"
                    >
                      {otp.map((digit, index) => (
                        <Input
                          key={index}
                          ref={(el) => (inputRefs.current[index] = el)}
                          w={{ base: "40px", sm: "45px", md: "50px" }}
                          h={{ base: "50px", sm: "56px" }}
                          textAlign="center"
                          fontSize={{ base: "xl", md: "2xl" }}
                          fontWeight="800"
                          bg={fieldBg}
                          borderRadius="lg"
                          border="2px solid"
                          borderColor={fieldBorder}
                          _focus={{
                            borderColor: "brand-primary",
                            bg: "accent-surface",
                            boxShadow: "none",
                          }}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          maxLength={1}
                          type="tel"
                          p={0}
                        />
                      ))}
                    </HStack>

                    <Button
                      w="full"
                      className="btn-premium"
                      sx={{
                        backgroundColor: "#4f46e5 !important",
                        color: "white !important",
                        _hover: {
                          backgroundColor: "#4338ca !important",
                          transform: "translateY(-2px)",
                          boxShadow: "xl",
                        },
                        _active: {
                          backgroundColor: "#3730a3 !important",
                          transform: "translateY(0)",
                        },
                      }}
                      size="lg"
                      height={{ base: "50px", md: "56px" }}
                      borderRadius="xl"
                      fontSize="md"
                      fontWeight="bold"
                      boxShadow="lg"
                      onClick={handleSignIn}
                      isLoading={isLoading}
                    >
                      Verify & Sign In
                    </Button>

                    <HStack justify="center" pt={2} flexWrap="wrap">
                      <Text fontSize="sm" color={strongTextColor}>
                        Didn't receive the code?
                      </Text>
                      <Button
                        variant="link"
                        color="accent-text"
                        fontSize="sm"
                        isDisabled={resendDisabled}
                        onClick={handleSendOtp}
                        fontWeight="700"
                      >
                        {resendDisabled
                          ? `Resend in ${resendTimer}s`
                          : "Resend Now"}
                      </Button>
                    </HStack>

                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<FiArrowLeft />}
                      color={strongTextColor}
                      onClick={() => setOtpSent(false)}
                      _hover={{ bg: "transparent", color: "accent-text" }}
                    >
                      Back to Login
                    </Button>
                  </VStack>
                </Fade>
              )}

              <VStack spacing={3} pt={4} w="full">
                <Divider borderColor={fieldBorder} />
                <Text fontSize="xs" color={strongTextColor} fontWeight="600">
                  &copy; {new Date().getFullYear()} VMukti Solutions. All rights
                  reserved.
                </Text>
              </VStack>
          </VStack>
        </ScaleFade>
      </Container>

      <ToastContainer position="top-right" />
    </Box>
  );
};

export default Login;
